import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const NameInitialPowerCalculator = () => {
  const [name, setName] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Initial Letter Meanings
  const initialLetterMeanings = {
    A: {
      power: 'Creative Power',
      traits: [
        'Independent',
        'Pioneering',
        'Self-starter',
        'Creative',
        'Original'
      ],
      description: 'A represents creative power and leadership. People with A initials are often independent, pioneering, and have a strong sense of self.'
    },
    B: {
      power: 'Balance Power',
      traits: [
        'Balanced',
        'Harmonious',
        'Diplomatic',
        'Adaptable',
        'Social'
      ],
      description: 'B represents balance and harmony. People with B initials are often diplomatic, adaptable, and have strong social skills.'
    },
    C: {
      power: 'Communication Power',
      traits: [
        'Expressive',
        'Communicative',
        'Charismatic',
        'Optimistic',
        'Energetic'
      ],
      description: 'C represents communication and charisma. People with C initials are often expressive, optimistic, and have strong communication skills.'
    },
    D: {
      power: 'Discipline Power',
      traits: [
        'Practical',
        'Reliable',
        'Disciplined',
        'Patient',
        'Steadfast'
      ],
      description: 'D represents discipline and practicality. People with D initials are often reliable, patient, and have strong organizational skills.'
    },
    E: {
      power: 'Expression Power',
      traits: [
        'Expressive',
        'Artistic',
        'Intuitive',
        'Idealistic',
        'Visionary'
      ],
      description: 'E represents expression and intuition. People with E initials are often artistic, intuitive, and have a strong sense of vision.'
    },
    F: {
      power: 'Family Power',
      traits: [
        'Family-oriented',
        'Supportive',
        'Caring',
        'Nurturing',
        'Loyal'
      ],
      description: 'F represents family and nurturing qualities. People with F initials are often caring, supportive, and have strong family values.'
    },
    G: {
      power: 'Growth Power',
      traits: [
        'Analytical',
        'Intelligent',
        'Curious',
        'Resourceful',
        'Strategic'
      ],
      description: 'G represents growth and intelligence. People with G initials are often analytical, curious, and have strong problem-solving skills.'
    },
    H: {
      power: 'Heart Power',
      traits: [
        'Heartfelt',
        'Sincere',
        'Empathetic',
        'Sensitive',
        'Passionate'
      ],
      description: 'H represents heart and emotion. People with H initials are often empathetic, sensitive, and have strong emotional intelligence.'
    },
    I: {
      power: 'Ideal Power',
      traits: [
        'Idealistic',
        'Visionary',
        'Innovative',
        'Creative',
        'Independent'
      ],
      description: 'I represents ideals and vision. People with I initials are often visionary, innovative, and have a strong sense of purpose.'
    },
    J: {
      power: 'Judgment Power',
      traits: [
        'Judicious',
        'Fair',
        'Decisive',
        'Organized',
        'Practical'
      ],
      description: 'J represents judgment and fairness. People with J initials are often decisive, fair, and have strong organizational skills.'
    },
    K: {
      power: 'Knowledge Power',
      traits: [
        'Knowledgeable',
        'Resourceful',
        'Practical',
        'Creative',
        'Versatile'
      ],
      description: 'K represents knowledge and practicality. People with K initials are often resourceful, practical, and have strong problem-solving skills.'
    },
    L: {
      power: 'Love Power',
      traits: [
        'Loving',
        'Affectionate',
        'Supportive',
        'Generous',
        'Kind'
      ],
      description: 'L represents love and affection. People with L initials are often loving, generous, and have strong emotional bonds.'
    },
    M: {
      power: 'Magnetism Power',
      traits: [
        'Magnetic',
        'Charismatic',
        'Social',
        'Expressive',
        'Creative'
      ],
      description: 'M represents magnetism and charisma. People with M initials are often magnetic, social, and have strong interpersonal skills.'
    },
    N: {
      power: 'Nurturing Power',
      traits: [
        'Nurturing',
        'Supportive',
        'Helpful',
        'Practical',
        'Organized'
      ],
      description: 'N represents nurturing qualities. People with N initials are often supportive, helpful, and have strong organizational skills.'
    },
    O: {
      power: 'Original Power',
      traits: [
        'Original',
        'Creative',
        'Expressive',
        'Artistic',
        'Visionary'
      ],
      description: 'O represents originality and creativity. People with O initials are often original, creative, and have strong artistic abilities.'
    },
    P: {
      power: 'Personal Power',
      traits: [
        'Personal',
        'Individual',
        'Independent',
        'Creative',
        'Expressive'
      ],
      description: 'P represents personal power and individuality. People with P initials are often independent, creative, and have strong personal drive.'
    },
    Q: {
      power: 'Quest Power',
      traits: [
        'Questing',
        'Inquisitive',
        'Analytical',
        'Resourceful',
        'Strategic'
      ],
      description: 'Q represents quest and exploration. People with Q initials are often inquisitive, analytical, and have strong problem-solving skills.'
    },
    R: {
      power: 'Rise Power',
      traits: [
        'Rising',
        'Progressive',
        'Forward-thinking',
        'Ambitious',
        'Determined'
      ],
      description: 'R represents rise and progress. People with R initials are often ambitious, determined, and have strong leadership qualities.'
    },
    S: {
      power: 'Spirit Power',
      traits: [
        'Spiritual',
        'Intuitive',
        'Creative',
        'Expressive',
        'Artistic'
      ],
      description: 'S represents spirit and intuition. People with S initials are often spiritual, intuitive, and have strong creative abilities.'
    },
    T: {
      power: 'Talent Power',
      traits: [
        'Talented',
        'Creative',
        'Expressive',
        'Artistic',
        'Original'
      ],
      description: 'T represents talent and creativity. People with T initials are often talented, creative, and have strong artistic abilities.'
    },
    U: {
      power: 'Understanding Power',
      traits: [
        'Understanding',
        'Empathetic',
        'Supportive',
        'Nurturing',
        'Compassionate'
      ],
      description: 'U represents understanding and compassion. People with U initials are often empathetic, supportive, and have strong emotional intelligence.'
    },
    V: {
      power: 'Vision Power',
      traits: [
        'Visionary',
        'Creative',
        'Expressive',
        'Artistic',
        'Original'
      ],
      description: 'V represents vision and creativity. People with V initials are often visionary, creative, and have strong artistic abilities.'
    },
    W: {
      power: 'Wisdom Power',
      traits: [
        'Wise',
        'Intelligent',
        'Analytical',
        'Resourceful',
        'Strategic'
      ],
      description: 'W represents wisdom and intelligence. People with W initials are often wise, analytical, and have strong problem-solving skills.'
    },
    X: {
      power: 'X-factor Power',
      traits: [
        'Unique',
        'Creative',
        'Expressive',
        'Artistic',
        'Original'
      ],
      description: 'X represents uniqueness and creativity. People with X initials are often unique, creative, and have strong artistic abilities.'
    },
    Y: {
      power: 'Youth Power',
      traits: [
        'Youthful',
        'Energetic',
        'Creative',
        'Expressive',
        'Artistic'
      ],
      description: 'Y represents youth and energy. People with Y initials are often energetic, creative, and have strong artistic abilities.'
    },
    Z: {
      power: 'Zeal Power',
      traits: [
        'Zealous',
        'Passionate',
        'Determined',
        'Ambitious',
        'Creative'
      ],
      description: 'Z represents zeal and passion. People with Z initials are often passionate, determined, and have strong creative drive.'
    }
  };

  const handleInputChange = (e) => {
    setName(e.target.value);
    setError('');
  };

  const calculateInitialPower = () => {
    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }

    const firstName = name.split(' ')[0];
    const initial = firstName.charAt(0).toUpperCase();

    if (!initialLetterMeanings[initial]) {
      setError('Invalid initial letter');
      return;
    }

    const initialData = initialLetterMeanings[initial];
    setResults({
      initial,
      power: initialData.power,
      traits: initialData.traits,
      description: initialData.description
    });

    // Prepare chart data
    const initialChartData = {
      labels: ['Power', 'Traits'],
      datasets: [{
        label: 'Initial Power Distribution',
        data: [70, 30], // Power is 70% and Traits is 30%
        backgroundColor: [
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)'
        ],
        borderColor: [
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1,
        hoverOffset: 4
      }]
    };

    setChartData(initialChartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Name Initial Power Calculator | Calculate Your Name's Power</title>
        <meta name="description" content="Calculate the power and significance of your name's initial letter using our Numerology Calculator. Understand your personality traits and potential." />
        <meta name="keywords" content="name initial power, numerology calculator, personality traits, name analysis, initial letter meaning" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/name-initial" />
        <meta property="og:title" content="Name Initial Power Calculator | Calculate Your Name's Power" />
        <meta property="og:description" content="Calculate the power and significance of your name's initial letter using our Numerology Calculator. Understand your personality traits and potential." />
        <meta property="og:image" content="https://yourwebsite.com/images/name-initial-calculator.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/name-initial" />
        <meta property="twitter:title" content="Name Initial Power Calculator | Calculate Your Name's Power" />
        <meta property="twitter:description" content="Calculate the power and significance of your name's initial letter using our Numerology Calculator. Understand your personality traits and potential." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/name-initial-calculator.png" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🌟</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Name Initial Power Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Calculate Your Name's Power</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Your Name:
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={name}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter your name"
          />
        </div>

        <button
          onClick={calculateInitialPower}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Name Initial Power
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Name Initial Power</h3>
            
            <div className="space-y-12">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="space-y-8">
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Initial Letter:</span>
                      <span className="font-medium text-purple-600 text-2xl">{results.initial}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Power Type:</h5>
                    <p className="text-purple-600 text-xl">{results.power}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Key Traits:</h5>
                    <ul className="list-disc pl-6 space-y-2">
                      {results.traits.map((trait, index) => (
                        <li key={index} className="text-gray-700">{trait}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Description:</h5>
                    <p className="text-gray-700">{results.description}</p>
                  </div>
                </div>
              </div>

              {/* Power Visualization Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Power Visualization</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Doughnut
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Initial Power Distribution',
                          color: '#4F46E5',
                          font: {
                            size: 16
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#4B5563',
                            font: {
                              size: 12
                            }
                          }
                        }
                      },
                      cutout: '60%',
                      animation: {
                        animateRotate: true,
                        animateScale: true
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Examples & Interpretations <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 1: Creative Power</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Name: Alexander</p>
                <p className="text-purple-600 text-2xl mb-2">Initial: A</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This name indicates strong creative power, independence, and pioneering spirit.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 2: Communication Power</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Name: Christopher</p>
                <p className="text-purple-600 text-2xl mb-2">Initial: C</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This name suggests strong communication skills, expressiveness, and charisma.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your name initial power:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Your Name</h3>
              <p className="text-gray-700">
                Input your full name in the input field.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Name Initial Power" button to see your results.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your initial letter's power type, key traits, and description to understand your personality potential.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is Name Initial Power?</h3>
              <p className="text-gray-700">
                Name Initial Power analyzes the significance and power of your name's first letter to reveal your personality traits, strengths, and potential.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is it calculated?</h3>
              <p className="text-gray-700">
                The calculator takes your name's first letter and matches it with its corresponding power type, traits, and description based on numerological principles.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What does it reveal?</h3>
              <p className="text-gray-700">
                It shows your personality traits, strengths, challenges, and potential based on the power of your name's initial letter.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Initial letter power calculation</li>
                <li>Personality trait analysis</li>
                <li>Comprehensive descriptions</li>
                <li>Detailed interpretations</li>
                <li>Personalized insights</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand personality traits</li>
                <li>Make informed decisions</li>
                <li>Enhance self-awareness</li>
                <li>Support personal growth</li>
                <li>Improve relationships</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NameInitialPowerCalculator;
